import "./index.css";
import { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  getDoc,
  updateDoc,
  setDoc
} from "firebase/firestore";
import { db } from "./firebaseConfig";

function Intervencoes() {
  const [interventions, setInterventions] = useState([]);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [speakers, setSpeakers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({
    id: "",
    title: "",
    speaker: "",
    program: "",
    order: ""
  });
  const fileRef = useRef(null);

  const fetchInterventions = async () => {
    try {
      const [intervSnap, speakersSnap, programsSnap] = await Promise.all([
        getDocs(query(collection(db, "evento_intervencoes"), orderBy("ordem", "asc"))),
        getDocs(query(collection(db, "evento_oradores"), orderBy("nome", "asc"))),
        getDocs(query(collection(db, "evento_programa"), orderBy("sessao", "asc")))
      ]);

      const speakerList = speakersSnap.docs.map(d => ({
        id: d.id,
        nome: d.data().nome || "(sem nome)"
      }));

      const programList = programsSnap.docs.map(d => ({
        id: d.id,
        sessao: d.data().sessao || "(sem sessão)"
      }));

      const list = await Promise.all(
        intervSnap.docs.map(async docItem => {
          const docData = docItem.data();

          let speakerName = "—", programSession = "—";

          if (docData.orador) {
            const refDoc = await getDoc(docData.orador);
            if (refDoc.exists()) speakerName = refDoc.data().nome || "—";
          }

          if (docData.programa) {
            const refDoc = await getDoc(docData.programa);
            if (refDoc.exists()) programSession = refDoc.data().sessao || "—";
          }

          return {
            id: docItem.id,
            ...docData,
            speakerName,
            programSession
          };
        })
      );

      setSpeakers(speakerList);
      setPrograms(programList);
      setInterventions(list);
    } catch {
      setError("Erro ao carregar as intervenções.");
    }
  };

  useEffect(() => {
    fetchInterventions();
  }, []);

  const deleteIntervention = async id => {
    if (!window.confirm("Deseja apagar esta intervenção?")) return;

    await deleteDoc(doc(db, "evento_intervencoes", id));
    setInterventions(prev => prev.filter(i => i.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const exportCSV = () => {
    const headers = ["id", "orador", "programa", "titulo", "ordem"];

    const rows = interventions.length
      ? interventions.map(d => [
          d.id || "",
          d.orador?.id || "",
          d.programa?.id || "",
          d.titulo || "",
          d.ordem || ""
        ])
      : [["", "", "", "", ""]];

    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(";")).join("\n");

    const bom = "\uFEFF";
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "intervencoes.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const detectSeparator = line => (line.includes(";") ? ";" : ",");

  const importCSV = async e => {
    const file = e.target.files[0];
    if (!file) return;

    window.alert("A sua importação está em processamento. Por favor, aguarde...");

    try {
      const text = await file.text();
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) throw new Error("ficheiro vazio");

      const sep = detectSeparator(lines[0]);
      const headers = lines[0].split(sep).map(h => h.replace(/"/g, "").trim());

      if (!headers.includes("titulo") || !headers.includes("orador") || !headers.includes("programa"))
        throw new Error("estrutura inválida");

      let added = 0, updated = 0;

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(sep).map(v => v.replace(/^"|"$/g, "").trim());
        const obj = {};
        headers.forEach((h, j) => obj[h] = values[j] || "");

        const docId = obj.id || undefined;
        const refDoc = docId ? doc(db, "evento_intervencoes", docId) : doc(collection(db, "evento_intervencoes"));

        const dataToSave = {
          titulo: obj.titulo,
          ordem: obj.ordem ? parseInt(obj.ordem) : 0,
          orador: obj.orador ? doc(db, "evento_oradores", obj.orador) : null,
          programa: obj.programa ? doc(db, "evento_programa", obj.programa) : null
        };

        if (docId) {
          const docSnap = await getDoc(refDoc);
          if (docSnap.exists()) { await updateDoc(refDoc, dataToSave); updated++; }
          else { await setDoc(refDoc, dataToSave); added++; }
        } else {
          await addDoc(collection(db, "evento_intervencoes"), dataToSave); added++;
        }
      }

      await fetchInterventions();
      window.alert(`Importação concluída. Inseridos: ${added}, Atualizados: ${updated}.`);
    } catch (err) {
      console.error(err);
      window.alert("A sua importação falhou. Verifique a estrutura do CSV.");
    }

    e.target.value = null;
  };

  const saveEdit = async () => {
    if (!selected) return;

    try {
      const refDoc = doc(db, "evento_intervencoes", selected.id);

      const updateData = {
        titulo: form.title,
        ordem: parseInt(form.order) || 0,
        orador: form.speaker ? doc(db, "evento_oradores", form.speaker) : null,
        programa: form.program ? doc(db, "evento_programa", form.program) : null
      };

      await updateDoc(refDoc, updateData);
      await fetchInterventions();
      setSelected(null);
    } catch {
      window.alert("Erro ao guardar a intervenção.");
    }
  };

  const filteredInterventions = interventions.filter(
    i =>
      i.titulo?.toLowerCase().includes(search.toLowerCase()) ||
      i.programSession?.toLowerCase().includes(search.toLowerCase())
  );

  if (error) return <p>{error}</p>;

  return (
    <div className="table-container">
      <div className="filtros">
        <input
          type="text"
          placeholder="Pesquisar por título ou sessão"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input"
        />

        <button className="btn btn-other" onClick={exportCSV}>
          Exportar CSV
        </button>

        <label className="btn btn-other">
          Importar CSV
          <input
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={importCSV}
            ref={fileRef}
          />
        </label>
      </div>

      {filteredInterventions.length === 0 ? (
        <p style={{ textAlign: "center", color: "#6b7280" }}>Sem dados</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Orador</th>
              <th>Programa</th>
              <th>Título</th>
              <th>Ordem</th>
              <th>Ações</th>
            </tr>
          </thead>

          <tbody>
            {filteredInterventions.map(item => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.speakerName || "—"}</td>
                <td>{item.programSession || "—"}</td>
                <td>{item.titulo || "—"}</td>
                <td>{item.ordem || "—"}</td>
                <td>
                  <button
                    className="btn btn-add"
                    onClick={() => {
                      setSelected(item);
                      setForm({
                        id: item.id,
                        title: item.titulo,
                        speaker: item.orador?.id || "",
                        program: item.programa?.id || "",
                        order: item.ordem
                      });
                    }}
                  >
                    Editar
                  </button>

                  <button
                    className="btn btn-delete"
                    onClick={() => deleteIntervention(item.id)}
                  >
                    Apagar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <div className="drawer">
          <div className="drawer-content">
            <label>ID:</label>
            <input type="text" value={form.id} disabled />

            <br /><br />

            <label>Título:</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
            />

            <br /><br />

            <label>Orador (ID do documento):</label>
            <input
              type="text"
              placeholder="Ex: yLFnWhTKvx6yCnOnbcWj"
              value={form.speaker}
              onChange={e => setForm(prev => ({ ...prev, speaker: e.target.value }))}
            />

            <br /><br />

            <label>Programa (ID do documento):</label>
            <input
              type="text"
              placeholder="Ex: 39lI1wtqrqf13p2KMhna"
              value={form.program}
              onChange={e => setForm(prev => ({ ...prev, program: e.target.value }))}
            />

            <br /><br />

            <label>Ordem:</label>
            <input
              type="number"
              value={form.order}
              onChange={e => setForm(prev => ({ ...prev, order: e.target.value }))}
            />
          </div>

          <div className="drawer-footer">
            <button className="btn btn-add" onClick={saveEdit}>
              Guardar
            </button>

            <button className="btn btn-cancel" onClick={() => setSelected(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Intervencoes;
